import classNames from 'classnames';
import isNumber from 'lodash/isNumber';
import React, { memo, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { EMPTY_ARRAY, EMPTY_OBJ } from '../../constants';
import { useWindowSize } from '../../hooks/useWindowSize';
import { Scrollbars } from '../../mixins';
import { COLOR, fieldBorderRadius, FONT_SIZE, SIZE, tooltipPointerHeight } from '../../styles';
import { KeyActions } from '../KeyActions';
import { Popover } from '../Popover';
import { type PopupMenuProps } from './PopupMenu.types';
import PopupMenuItem from './PopupMenuItem';

const PopupMenu: React.FC<PopupMenuProps> = memo(
  ({
    autoFocus = true,
    closeMenu,
    dataTestId = 'popup-menu',
    parent,
    menuItems = EMPTY_ARRAY,
    visibilityHandler = () => true,
    handlerData = EMPTY_OBJ,
    className,
    colorMode = 'light',
    style = EMPTY_OBJ,
    rootClass = '',
    isPopupOpen,
    horizontalOffset = 0,
    verticalOffset = 0,
    showPointer = true,
  }) => {
    const [initialSelection, setInitialSelection] = useState(null);
    const [items, setItems] = useState({});
    const [previouslyFocused, setPreviouslyFocused] = useState([]);
    const [shortcuts] = useState([
      {
        shortcut: 'esc',
        action: () => {
          if (!parent) {
            closeMenu?.();
          }
        },
      },
      {
        shortcut: 'up',
        action: () => selectNextItem(),
      },
      {
        shortcut: 'down',
        action: () => selectPrevItem(),
      },
    ]);
    const [isSelectionInit, setIsSelectionInit] = useState(!autoFocus);
    const [selectedItem, setSelectedItem] = useState(autoFocus ? 0 : null);
    const [maxMenuHeight, setMaxMenuHeight] = useState(0);
    const [menuVerticalOffset, setMenuVerticalOffset] = useState(0);
    const [menuHorizontalOffset, setMenuHorizontalOffset] = useState(20);
    const [position, setPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuHeight, setMenuHeight] = useState(0);
    const [isPopupHidden, setIsPopupHidden] = useState(true);
    const popupListRef = useRef<HTMLUListElement | null>(null);

    const { height: windowHeight } = useWindowSize();

    useEffect(() => {
      if (!parent || !popupListRef.current) {
        setIsPopupHidden(false);
        return;
      }

      let newMaxHeight: number = 0;
      const toTopHeight = parent.getBoundingClientRect().top; // Height from top of window to top of parent button
      const toBottomHeight = windowHeight - parent.getBoundingClientRect().bottom; // Height from bottom of window to bottom of parent buttom

      let newPosition: 'bottom' | 'top' = 'bottom';
      newPosition = toTopHeight > toBottomHeight ? 'top' : 'bottom';
      setPosition(newPosition);

      const availableHeight = newPosition === 'top' ? toTopHeight : toBottomHeight;
      newMaxHeight = availableHeight - 20;

      let newVerticalOffset = verticalOffset ?? 0;

      newVerticalOffset =
        newPosition === 'top'
          ? newVerticalOffset - menuHeight - tooltipPointerHeight
          : newVerticalOffset;
      // Don't display the menu until its height and position are set correctly
      setIsPopupHidden(!menuHeight);
      setMaxMenuHeight(newMaxHeight);
      setMenuVerticalOffset(newVerticalOffset);
    }, [menuHeight, position]);

    // Watch the updates of menu height
    useEffect(() => {
      const resizeObserver = new ResizeObserver((entries) => {
        setMenuHeight(entries[0].target.clientHeight);
      });

      if (popupListRef.current) {
        resizeObserver.observe(popupListRef.current);
      }
      // Cleanup the observer on unmount
      return () => {
        if (popupListRef.current) {
          resizeObserver.unobserve(popupListRef.current);
        }
      };
    }, [popupListRef.current]);

    useEffect(() => {
      updateInitialSelection();
    }, []);

    useEffect(() => {
      saveFocusedElement();
      return () => focusOnPrevious();
    }, []);

    const focusOnPrevious = () => {
      for (const element of previouslyFocused) {
        if (element) {
          // eslint-disable-next-line
          // @ts-ignore
          element.focus();
          break;
        }
      }
      setPreviouslyFocused([]);
    };

    const saveFocusedElement = () => {
      const focused = document.activeElement;

      if (!focused) {
        return;
      }

      // Save the focused element
      // eslint-disable-next-line
      // @ts-ignore
      setPreviouslyFocused([...previouslyFocused, focused]);

      // Save the parent, as a backup
      if (focused.parentElement) {
        // eslint-disable-next-line
        // @ts-ignore
        setPreviouslyFocused([...previouslyFocused, focused.parentElement]);
      }

      // Save the first <a> or <button> of a parent .action element,
      // as a second backup.
      if (focused.closest('.action')) {
        setPreviouslyFocused([
          // eslint-disable-next-line
          // @ts-ignore
          ...previouslyFocused,
          // eslint-disable-next-line
          // @ts-ignore
          focused.closest('.action').querySelector('a, button'),
        ]);
      }
    };

    const updateInitialSelection = () => {
      // eslint-disable-next-line
      // @ts-ignore
      if (menuItems.length && !isSelectionInit && isNumber(initialSelection)) {
        setIsSelectionInit(true);
        setSelectedItem(initialSelection);
      }
    };

    const selectNextItem = (sourceSelection?: any) => {
      let newSelection = isNumber(sourceSelection) ? sourceSelection : selectedItem;

      // eslint-disable-next-line
      // @ts-ignore
      if (newSelection !== menuItems.length - 1) {
        // eslint-disable-next-line
        // @ts-ignore
        newSelection = newSelection + 1;
      } else {
        newSelection = 0;
      }

      // Check if this results in a non-action selection, and go on to the next option, if so
      const isBackToStart = newSelection === selectedItem;
      if (isBackToStart) {
        return;
      } else if (getItemType(newSelection) !== 'action') {
        selectNextItem(newSelection);
      } else {
        setSelectedItem(newSelection);
      }
    };

    const selectPrevItem = (sourceSelection?: any) => {
      let newSelection = isNumber(sourceSelection) ? sourceSelection : selectedItem;

      if (newSelection !== 0) {
        // eslint-disable-next-line
        // @ts-ignore
        newSelection = newSelection - 1;
      } else {
        // eslint-disable-next-line
        // @ts-ignore
        newSelection = menuItems.length - 1;
      }

      // Check if this results in a non-action selection, and go on to the next option, if so
      const isBackToStart = newSelection === selectedItem;
      if (isBackToStart) {
        return;
      } else if (getItemType(newSelection) !== 'action') {
        selectPrevItem(newSelection);
      } else {
        setSelectedItem(newSelection);
      }
    };

    // eslint-disable-next-line
    // @ts-ignore
    const getItemType = (index) => {
      // eslint-disable-next-line
      // @ts-ignore
      if (!menuItems[index]) {
        return null;
      }
      // eslint-disable-next-line
      // @ts-ignore
      const { disabled = false, isVisible, type } = menuItems[index];
      const isItemVisible =
        // eslint-disable-next-line
        // @ts-ignore
        typeof isVisible === 'boolean' ? isVisible : visibilityHandler({ isVisible });
      return isItemVisible && !disabled ? type : null;
    };

    // eslint-disable-next-line
    // @ts-ignore
    const setItemRef = (item, key) => {
      const newItems = { ...items };
      // eslint-disable-next-line
      // @ts-ignore
      newItems[key] = item;
      setItems(newItems);
    };

    // eslint-disable-next-line
    // @ts-ignore
    const renderMenuItem = (item, index) => {
      if (!item) {
        return;
      }
      const { focusOnOpen = false } = item;

      if (!isSelectionInit && focusOnOpen) {
        setInitialSelection(index);
      }

      return (
        <PopupMenuItem
          dataTestId={dataTestId}
          handlerData={handlerData}
          index={index}
          item={item}
          key={index}
          refFunc={setItemRef}
          selected={selectedItem === index}
          visibilityHandler={visibilityHandler}
          closeMenu={closeMenu}
          isPopupOpen={isPopupOpen}
        />
      );
    };
    /**
     * Handling if the popup overflows the viewport horizontally.
     * Although there is horizontal overflow handling in Popover.ts, it does not work.
     */
    useEffect(() => {
      const popupListRefParent = popupListRef.current?.parentElement;
      if (!popupListRefParent) return;

      const rect = popupListRefParent.getBoundingClientRect();
      const { innerWidth } = window;
      const WINDOW_MARGIN_PX = 8;
      // Check right overflow
      if (rect.right > innerWidth - WINDOW_MARGIN_PX)
        setMenuHorizontalOffset((old) => old - rect.right + innerWidth - WINDOW_MARGIN_PX);

      // Check left overflow
      if (rect.left < WINDOW_MARGIN_PX)
        setMenuHorizontalOffset((old) => old - rect.left + WINDOW_MARGIN_PX);
    }, []);
    const getPopup = () => {
      const menuClass = classNames('popup-menu', className);
      const wrapperClass = classNames(`mode-${colorMode}`, position, {
        'visibility-hidden': isPopupHidden,
      });
      return (
        <KeyActions actions={shortcuts}>
          <PopupListWrapper
            showPointer={showPointer}
            xOffset={menuHorizontalOffset}
            className={wrapperClass}
            style={style}
          >
            <PopupList
              ref={popupListRef}
              style={parent ? { maxHeight: maxMenuHeight } : {}}
              className={menuClass}
            >
              {
                // eslint-disable-next-line
                // @ts-ignore
                menuItems.map(renderMenuItem)
              }
            </PopupList>
          </PopupListWrapper>
        </KeyActions>
      );
    };

    return parent ? (
      <Popover
        className={rootClass}
        horizontalOffset={horizontalOffset}
        horizontalOffsetToMiddle={true}
        horizontalPosition="left"
        verticalOffset={menuVerticalOffset}
        isOpen={isPopupOpen}
        isOverlayVisible={false}
        onClose={closeMenu}
        parent={parent}
        verticalPosition={position}
      >
        {getPopup()}
      </Popover>
    ) : (
      getPopup()
    );
  },
);

interface PopupListWrapperProps {
  showPointer?: boolean;
  xOffset?: number;
}

const PopupList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-wrap: break-word;

  overflow: auto;
  overflow-x: hidden;

  ${Scrollbars({
    width: '8px',
    height: '8px',
    border: '0.5px',
    foregroundColor: 'rgba(150, 150, 150, 0.5)',
    backgroundColor: 'transparent',
  })};
`;

const PopupListWrapper = styled.div<PopupListWrapperProps>`
  border-radius: ${fieldBorderRadius};
  border: 1px solid ${COLOR['light-gray']};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.175);
  font-size: ${FONT_SIZE['13']};
  font-weight: normal;
  margin: 4px 0 0;
  padding: 4px 0;
  max-width: 260px;
  min-width: 160px;
  position: absolute;
  right: 0;
  z-index: 1000;
  ${({ xOffset }) =>
    xOffset &&
    css`
      transform: translateX(${xOffset}px);
    `}
  &.visibility-hidden {
    visibility: hidden;
  }

  ${({ showPointer, xOffset = 0 }) =>
    showPointer &&
    css`
      &:before,
      &:after {
        border-bottom: 7px solid ${COLOR['light-gray']};
        border-left: 7px solid transparent;
        border-right: 7px solid transparent;
        content: '';
        display: inline-block;
        right: ${xOffset - 7}px;
        position: absolute;
        top: -7px;
        z-index: 1001;
      }
      &:after {
        border-bottom: 6px solid ${COLOR['white']};
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        right: ${xOffset - 6}px;
        top: -6px;
        z-index: 1001;
      }
    `}

  &.mode-light {
    background-color: ${COLOR['white']};
    color: ${COLOR['primary']};

    svg.icon {
      fill: ${COLOR['primary']};
    }

    .menu-action,
    .menu-item.menu-action {
      &:not(.disabled) {
        &:hover,
        &:focus {
          svg.icon {
            fill: ${COLOR['white']};
          }
        }
      }
    }
  }

  &.mode-dark {
    background-color: ${COLOR['dark-gray-light']};
    border-color: transparent;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.175);
    color: ${COLOR['white']};

    svg.icon {
      fill: ${COLOR['white']};
    }

    .menu-divider {
      opacity: 0.4;
    }

    .menu-action,
    .menu-item.menu-action {
      &:not(.disabled) {
        &:hover,
        &:focus {
          background: ${COLOR['turquoise']};
          color: ${COLOR['turquoise-darker']};

          .icon {
            color: ${COLOR['turquoise-darker']};
          }

          svg.icon {
            fill: ${COLOR['turquoise-darker']};
          }
        }
      }
    }

    ${({ showPointer }) =>
      showPointer &&
      css`
        &:before,
        &:after {
          border-bottom-color: ${COLOR['dark-gray-light']};
        }

        &.top {
          &:before {
            border-top: 7px solid ${COLOR['dark-gray-light']};
          }
          &:after {
            border-top: 6px solid ${COLOR['dark-gray-light']};
          }
        }
      `}
  }

  &.top {
    margin: -5px 0 0;
    ${({ showPointer }) =>
      showPointer &&
      css`
        &:before {
          border-bottom: none;
          border-top: 7px solid ${COLOR['light-gray']};
          bottom: -7px;
          top: auto;
        }

        &:after {
          border-bottom: none;
          border-top: 6px solid ${COLOR['white']};
          bottom: -6px;
          top: auto;
        }
      `}
  }

  svg.icon,
  .icon {
    margin-right: ${SIZE['8']};
  }

  &.portal-popup {
    box-shadow: none;
    position: relative;
  }

  .menu-divider {
    border-bottom: 1px solid ${COLOR['light-gray']};
    margin-bottom: 5px;
    padding-top: 5px;
    opacity: 0.6;
  }
`;

// Display Name
PopupMenu.displayName = 'PopupMenu';

export default PopupMenu;
